import * as React from "react";
import styled from "@emotion/styled";
import classNames from "classnames";
import FormContainer from "@/components/atoms/FormContainer";
import FormBlock from "@/components/molecules/FormBlock";
import Article from "@/components/molecules/Article";
import MiniTitle from "@/components/atoms/MiniTitle";
import Title from "@/components/atoms/Title";
import { validateCustomer as validator } from "@/utils/validation";
import { useFormHandler, useFormHandlerToggleRequired, useQueryParams } from "@/utils/hooks";
import FormContext, { initFormStore } from "@/contexts/Form";
import form from "@/utils/form";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import contract, {
  CONTACT_PREFERENCE_POSITION,
  ContactPreferenceExtras,
  Feature,
  getSentence,
  HIDDEN_COMPONENT_OPTIONS,
  isHiddenComponent,
} from "@/utils/contract";
import {
  prefectureOptions,
  switchPrefectureCode,
  IPrefecture,
  PrefectureEnum,
  getShopRelatedPrefectureOptions,
} from "@/constants/Prefecture";
import { getShopOptions, getShopPrefId } from "@/constants/Shop";
import { getReferralSourceOptions } from "@/constants/ReferralSource";
import QueryKey from "@/constants/QueryKey";

import { useTheme } from "@/hooks/useTheme";

const contactTimeOptions: Option[] = [
  { label: "指定なし", value: "指定なし" },
  { label: "平日午前", value: "平日午前" },
  { label: "平日午後", value: "平日午後" },
  { label: "平日夜中", value: "平日夜中" },
  { label: "休日午前", value: "休日午前" },
  { label: "休日午後", value: "休日午後" },
  { label: "休日夜中", value: "休日夜中" },
];

const IntersectionObserverContainer = styled.div`
  opacity: 0;

  &.isVisible {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;

const CustomFormBlock = styled(FormBlock)`
  .label-container {
    width: 194px; // PCの場合の幅を設定
  }
  @media (max-width: 768px) {
    .label-container {
      width: auto; // スマートフォンの場合は幅を自動に設定
    }
  }
`;

/**
 * チェックボックスを表示するか. extras が不正値だった場合、デフォルト表示位置である CustomerBlock で表示するため、 true を返している
 * @param contactPreference
 */
const isCheckboxShow = (contactPreference: { has: boolean; extras: ContactPreferenceExtras }): boolean => {
  if (!contactPreference.has || !contactPreference.extras) {
    // 設定自体が未設定の場合は表示しない
    return false;
  }
  const { position } = contactPreference.extras;
  if (!position) {
    return true;
  }
  const isCorrectValue = Object.values(CONTACT_PREFERENCE_POSITION).includes(position);
  if (!isCorrectValue) {
    return true;
  }
  return position === CONTACT_PREFERENCE_POSITION.CUSTOMER_BLOCK;
};

const CustomerBlock = () => {
  const query = useQueryParams();
  const shopId = query.get(QueryKey.SHOP_SELECTION);
  const { COLOR_THEME, isNewTheme } = useTheme();
  const {
    state: { values, validates },
    dispatch,
  } = React.useContext(FormContext) || initFormStore(form);

  // START: 機能のための初期値 ===================================================
  const {
    state: { contractId, featuresObj, shopInfo, referralSource },
  } = React.useContext(ContractContext) || initContractStore(contract);
  const TEL_REQUIRED = !featuresObj[Feature.TEL_OPTIONAL];

  const isDirectResult = Boolean(featuresObj[Feature.DIRECT_RESULT]);

  const isShop = Boolean(featuresObj[Feature.SHOP_SETTINGS]?.enabled);

  const isReferralSource = Boolean(featuresObj[Feature.REFERRAL_SOURCE]?.enabled);
  const referralSourceText = featuresObj[Feature.REFERRAL_SOURCE]?.text ?? "";

  const CONTACT_PREFERENCE_TEL: { has: boolean; extras: ContactPreferenceExtras } = {
    has: Boolean(featuresObj[Feature.CONTACT_PREFERENCE_TEL]),
    extras: featuresObj[Feature.CONTACT_PREFERENCE_TEL],
  };

  const CONTACT_PREFERENCE_MAIL: { has: boolean; extras: ContactPreferenceExtras } = {
    has: Boolean(featuresObj[Feature.CONTACT_PREFERENCE_MAIL]),
    extras: featuresObj[Feature.CONTACT_PREFERENCE_MAIL],
  };

  const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);

  const hiddenCustomerKanaComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_KANA);

  const hiddenCustomerTelComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_TEL);

  const hiddenCustomerPostCode = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_POST_CODE);

  // END: 機能のための初期値 =====================================================

  const ref = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  const handleLastName = useFormHandler("lastName", validator.lastName);
  const handleFirstName = useFormHandler("firstName", validator.firstName);
  const handleLastNameKana = useFormHandler("lastNameKana", validator.lastNameKana);
  const handleFirstNameKana = useFormHandler("firstNameKana", validator.firstNameKana);
  const handleMailAddress = useFormHandler("mailAddress", validator.mailAddress);
  const handleIsSame = useFormHandler("isSame");

  const handleConfirmMailAddress = (value: string) => {
    dispatch({
      type: "UPDATE_CONFIRM_MAIL_ADDRESS",
      payload: {
        value,
      },
    });
  };

  const handleMailAddresses = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    handleMailAddress(event.target.value);
    if (values.confirmMailAddress) {
      handleConfirmMailAddress(values.confirmMailAddress);
    }
  };
  const handleTel = useFormHandlerToggleRequired("tel", validator.tel);
  const handleCanContactTel = useFormHandler("canContactTel");
  const handleCanContactMail = useFormHandler("canContactMail");
  const handleContactTime = useFormHandler("contactTime");
  const handlePostCode = useFormHandler("postCode", validator.postCode);
  const handlePref = useFormHandler("pref", validator.pref);
  const handleShopPref = useFormHandler("shopPrefId", validator.shopPrefId);
  const handleShop = useFormHandler("shopId", validator.shopId);
  const handleCity = useFormHandler("city", validator.city);
  const handleTown = useFormHandler("town", validator.town);
  const handleStreet = useFormHandler("street", validator.street);
  const handleBuilding = useFormHandler("building", validator.building);
  const handleReferralSourceCd = useFormHandler("referralSourceCd", validator.referralSourceCd);
  const shopPrefInfo = getShopRelatedPrefectureOptions(shopInfo, contractId as number);

  React.useEffect(() => {
    if (shopId && isShop && Array.isArray(shopPrefInfo) && shopPrefInfo.length > 0) {
      const shopPrefId = getShopPrefId(shopInfo, shopId);
      if (shopPrefId) {
        handleShopPref(PrefectureEnum[shopPrefId]);
      }
      handleShop(shopId);
    }
  }, [shopId]);

  const isMailCheckboxShow = isCheckboxShow(CONTACT_PREFERENCE_MAIL);
  const isNegative =
    CONTACT_PREFERENCE_MAIL.has &&
    CONTACT_PREFERENCE_MAIL.extras.sentence &&
    CONTACT_PREFERENCE_MAIL.extras.sentence.use === "negative";
  const description = CONTACT_PREFERENCE_MAIL.has ? CONTACT_PREFERENCE_MAIL.extras.description : "";
  const checkboxProps = {
    value: values.canContactMail ? "on" : "off",
    description,
    name: "canContactMail",
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => handleCanContactMail(ev.target.value === "off"),
    checked: Boolean(values.canContactMail),
    text: getSentence(CONTACT_PREFERENCE_MAIL.extras) || "今後当社からのメール配信を希望する",
    checkboxColor: COLOR_THEME.extras?.mail_checkbox,
  };
  if (isNegative) {
    checkboxProps.value = !values.canContactMail ? "on" : "off";
    checkboxProps.onChange = ev => handleCanContactMail(ev.target.value !== "off");
    checkboxProps.checked = Boolean(!values.canContactMail);
    checkboxProps.text = getSentence(CONTACT_PREFERENCE_MAIL.extras) || "今後当社からのメール配信を希望しない";
  }

  if (typeof IntersectionObserver !== "undefined") {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ isIntersecting }) => setIsVisible(isIntersecting));
    });

    React.useEffect(() => {
      if (ref.current) {
        observer.observe(ref.current);
      }
      return () => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    });
  }

  const formBlockProps = {
    className: `${isNewTheme ? "underline form" : "underline"}`,
    backgroundColor: "#ffffff",
  };

  const isCssOverride = classNames({
    override: isNewTheme,
  });

  const renderMiniTitle = () => {
    return (
      <Article.Header>
        {!isNewTheme ? (
          <MiniTitle>査定結果のご連絡先を入力してください</MiniTitle>
        ) : (
          <Title color={COLOR_THEME.extras?.secondary}>ご連絡先をご入力ください</Title>
        )}
      </Article.Header>
    );
  };

  const contactInfo = {
    old: {
      kanji: {
        label: "お名前（漢字）",
        surname: "姓",
        firstname: "名",
      },
      kana: {
        label: "お名前（カナ）",
        surname: "セイ",
        firstname: "メイ",
      },
    },
    new: {
      kanji: {
        label: "お名前",
        surname: "姓（漢字）",
        firstname: "名（漢字）",
      },
      kana: {
        label: " ",
        surname: "セイ（カナ）",
        firstname: "メイ（カナ）",
      },
    },
  };

  const transKey = isNewTheme ? "new" : "old";

  const getKanaNameRenderCheck = () => {
    if (!isNewTheme) {
      return true;
    } else {
      return hiddenCustomerKanaComponent;
    }
  };

  const getOldKanaNameRenderCheck = () => {
    if (!isNewTheme) {
      return hiddenCustomerKanaComponent;
    } else {
      return true;
    }
  };

  const sameAsAddress = () => {
    if (!isNewTheme) {
      return true;
    } else {
      return hiddenLivingAddressComponent;
    }
  };

  const telField = () => {
    if (isNewTheme) {
      return (
        <FormBlock.Row hidden={!CONTACT_PREFERENCE_TEL.has}>
          {CONTACT_PREFERENCE_TEL.has && CONTACT_PREFERENCE_TEL.extras.sentence?.use === "negative" ? (
            <FormBlock.Checkbox2
              value={!values.canContactTel ? "on" : "off"}
              name="canContactTel"
              onChange={ev => handleCanContactTel(ev.target.value !== "off")}
              checked={Boolean(!values.canContactTel)}
              text={getSentence(CONTACT_PREFERENCE_TEL.extras) ?? "営業担当からの電話での連絡を希望しない"}
              disabled={!values.tel || Boolean(validates.tel)}
            />
          ) : (
            <FormBlock.Checkbox2
              value={values.canContactTel ? "on" : "off"}
              name="canContactTel"
              onChange={ev => handleCanContactTel(ev.target.value === "off")}
              checked={Boolean(values.canContactTel)}
              text={getSentence(CONTACT_PREFERENCE_TEL.extras) ?? "営業担当からの電話での連絡を希望する"}
              disabled={!values.tel || Boolean(validates.tel)}
            />
          )}
        </FormBlock.Row>
      );
    }
    return (
      <FormBlock.Row hidden={!CONTACT_PREFERENCE_TEL.has}>
        {CONTACT_PREFERENCE_TEL.has && CONTACT_PREFERENCE_TEL.extras.sentence?.use === "negative" ? (
          <FormBlock.Checkbox
            value={!values.canContactTel ? "on" : "off"}
            name="canContactTel"
            onChange={ev => handleCanContactTel(ev.target.value !== "off")}
            checked={Boolean(!values.canContactTel)}
            text={getSentence(CONTACT_PREFERENCE_TEL.extras) ?? "営業担当からの電話での連絡を希望しない"}
            disabled={!values.tel || Boolean(validates.tel)}
          />
        ) : (
          <FormBlock.Checkbox
            value={values.canContactTel ? "on" : "off"}
            name="canContactTel"
            onChange={ev => handleCanContactTel(ev.target.value === "off")}
            checked={Boolean(values.canContactTel)}
            text={getSentence(CONTACT_PREFERENCE_TEL.extras) ?? "営業担当からの電話での連絡を希望する"}
            disabled={!values.tel || Boolean(validates.tel)}
          />
        )}
      </FormBlock.Row>
    );
  };

  return (
    <IntersectionObserverContainer className={classNames({ isVisible })} ref={ref}>
      <Article>
        {renderMiniTitle()}
        <Article.Block className={isNewTheme ? "border-none bgWhite" : ""}>
          <FormContainer className={isCssOverride}>
            <FormBlock {...formBlockProps}>
              <FormBlock.Label
                value={contactInfo[transKey].kanji.label}
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
                borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
              />
              <FormBlock.Row className={isNewTheme ? "displayRow" : ""}>
                <FormBlock.Input
                  placeholder="例）鈴木"
                  description={contactInfo[transKey].kanji.surname}
                  onChange={ev => handleLastName(ev.target.value)}
                  inputWrapperClassName={isNewTheme ? "nameField" : ""}
                  width={isNewTheme ? "200px" : "40%"}
                  className={classNames({
                    inputCss5: isNewTheme,
                  })}
                  value={values.lastName}
                  required
                  validateMessage={validates.lastName}
                  aria-label="名前 姓"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
                <FormBlock.Input
                  placeholder="例）太郎"
                  description={contactInfo[transKey].kanji.firstname}
                  onChange={ev => handleFirstName(ev.target.value)}
                  width={isNewTheme ? "200px" : "40%"}
                  className={classNames({
                    inputCss5: isNewTheme,
                  })}
                  value={values.firstName}
                  required
                  validateMessage={validates.firstName}
                  aria-label="名前 名"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row className={isNewTheme ? "displayRow" : ""} hidden={getKanaNameRenderCheck()}>
                <FormBlock.Input
                  placeholder="例）スズキ"
                  description={contactInfo[transKey].kana.surname}
                  onChange={ev => handleLastNameKana(ev.target.value)}
                  width={isNewTheme ? "200px" : "40%"}
                  inputWrapperClassName={isNewTheme ? "nameField" : ""}
                  className={classNames({
                    inputCss5: isNewTheme,
                  })}
                  value={values.lastNameKana}
                  required
                  validateMessage={validates.lastNameKana}
                  aria-label="名前カナ 姓"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
                <FormBlock.Input
                  placeholder="例）タロウ"
                  description={contactInfo[transKey].kana.firstname}
                  onChange={ev => handleFirstNameKana(ev.target.value)}
                  width={isNewTheme ? "200px" : "40%"}
                  className={classNames({
                    inputCss5: isNewTheme,
                  })}
                  value={values.firstNameKana}
                  required
                  validateMessage={validates.firstNameKana}
                  aria-label="名前カナ 名"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
            </FormBlock>
            <FormBlock {...formBlockProps} hidden={getOldKanaNameRenderCheck()}>
              <FormBlock.Label
                value={contactInfo[transKey].kana.label}
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
                borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  placeholder="例）スズキ"
                  description={contactInfo[transKey].kana.surname}
                  onChange={ev => handleLastNameKana(ev.target.value)}
                  width="40%"
                  value={values.lastNameKana}
                  required
                  validateMessage={validates.lastNameKana}
                  aria-label="名前カナ 姓"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
                <FormBlock.Input
                  placeholder="例）タロウ"
                  description={contactInfo[transKey].kana.firstname}
                  onChange={ev => handleFirstNameKana(ev.target.value)}
                  width="40%"
                  value={values.firstNameKana}
                  required
                  validateMessage={validates.firstNameKana}
                  aria-label="名前カナ 名"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
            </FormBlock>
            <FormBlock {...formBlockProps} hidden={hiddenCustomerTelComponent}>
              <FormBlock.Label
                value="電話番号"
                required={TEL_REQUIRED}
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
                borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  placeholder="例）0312345678"
                  onChange={ev => handleTel(ev.target.value, TEL_REQUIRED)}
                  width={isNewTheme ? "200px" : "50%"}
                  className={classNames({
                    inputCss2: isNewTheme,
                  })}
                  value={values.tel}
                  description="電話番号"
                  required={TEL_REQUIRED}
                  validateMessage={validates.tel}
                  aria-label="電話番号"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row hidden={isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CONTACT_TIME)}>
                <FormBlock.Select
                  onChange={ev => handleContactTime(ev.target.value)}
                  width={isNewTheme ? "200px" : "50%"}
                  className={classNames({
                    selectCss: isNewTheme,
                  })}
                  value={values.contactTime}
                  options={contactTimeOptions}
                  description="連絡希望時間帯"
                  prompt={false}
                  aria-label="連絡希望時間帯"
                  disabled={!values.tel || Boolean(validates.tel)}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
              {telField()}
            </FormBlock>
            <FormBlock {...formBlockProps}>
              <FormBlock.Label
                value="メールアドレス"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
                borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  placeholder="例）taro-suzuki@example.com"
                  onChange={ev => {
                    handleMailAddresses(ev);
                  }}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  value={values.mailAddress}
                  required
                  validateMessage={validates.mailAddress}
                  aria-label="メールアドレス"
                  description={isDirectResult ? "" : "こちらに査定結果のURLをお送りします"}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  placeholder="例）taro-suzuki@example.com"
                  onChange={ev => handleConfirmMailAddress(ev.target.value)}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  value={values.confirmMailAddress}
                  required
                  validateMessage={validates.confirmMailAddress}
                  description="メールアドレス（確認用の再入力）"
                  aria-label="メールアドレス（確認用の再入力）"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>

              <FormBlock.Row hidden={!isMailCheckboxShow}>
                {isNewTheme ? (
                  <FormBlock.Checkbox2
                    {...checkboxProps}
                    checkboxColor={{
                      text: "#333333",
                      checkbox: {
                        checked: "#0275ff",
                        unchecked: "#f2f2f2",
                      },
                    }}
                  />
                ) : (
                  <FormBlock.Checkbox {...checkboxProps} />
                )}
              </FormBlock.Row>
            </FormBlock>
            <FormBlock {...formBlockProps} hidden={hiddenLivingAddressComponent}>
              <FormBlock.Label
                value={isNewTheme ? "ご住所" : "お住いの住所"}
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
                borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
              />
              <FormBlock.Row hidden={sameAsAddress()}>
                {isNewTheme ? (
                  <FormBlock.Checkbox2
                    value={values.isSame ? "はい" : "いいえ"}
                    name="currentaddress"
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                      handleIsSame(ev.target.value === "はい" ? false : true);
                    }}
                    checked={values.isSame}
                    text="査定物件の所在地と同じ"
                    disabled={false}
                    labelFontSize="14px"
                    labelfontWeight="400"
                  />
                ) : (
                  <FormBlock.Checkbox
                    value={values.isSame ? "はい" : "いいえ"}
                    name="currentaddress"
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                      handleIsSame(ev.target.value === "はい" ? false : true);
                    }}
                    checked={values.isSame}
                    text="査定物件の所在地と同じ"
                    disabled={false}
                    labelFontSize="14px"
                    labelfontWeight="400"
                  />
                )}
              </FormBlock.Row>
              <FormBlock.Row hidden={hiddenCustomerPostCode}>
                <FormBlock.Input
                  placeholder="例）101-0034"
                  onChange={ev => handlePostCode(ev.target.value)}
                  description="郵便番号"
                  width={isNewTheme ? "200px" : "30%"}
                  className={classNames({
                    inputCss2: isNewTheme,
                  })}
                  value={values.postCode}
                  required={!hiddenCustomerPostCode}
                  validateMessage={validates.postCode}
                  aria-label="郵便番号"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Select
                  description="都道府県"
                  value={values.pref}
                  options={prefectureOptions}
                  onChange={ev => handlePref(ev.target.value)}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  required
                  validateMessage={validates.pref}
                  aria-label="お住いの都道府県"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  description="市区町村"
                  placeholder="例）千代田区"
                  onChange={ev => handleCity(ev.target.value)}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  value={values.city}
                  required
                  validateMessage={validates.city}
                  aria-label="お住いの市区町村"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  description="町丁目"
                  placeholder="例）中央通り1丁目"
                  onChange={ev => handleTown(ev.target.value)}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  value={values.town}
                  required
                  validateMessage={validates.town}
                  aria-label="お住いの町丁目"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  description="番地"
                  placeholder="例）1-1"
                  onChange={ev => handleStreet(ev.target.value)}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  value={values.street}
                  required
                  validateMessage={validates.street}
                  aria-label="お住いの番地"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  description={isNewTheme ? "マンション名・部屋番号" : "建物名 号室"}
                  placeholder="例）SREマンション 101号室"
                  onChange={ev => handleBuilding(ev.target.value)}
                  width={isNewTheme ? "400px" : "50%"}
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  value={values.building}
                  validateMessage={validates.building}
                  aria-label="お住いの建物名 号室"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  boarderRadius={COLOR_THEME.extras?.invalid_state?.boarderRadius}
                  borderColor={COLOR_THEME.extras?.invalid_state?.borderColor}
                />
              </FormBlock.Row>
            </FormBlock>
            {isShop && (
              <FormBlock {...formBlockProps}>
                <FormBlock.Label
                  value="お問い合わせ店舗"
                  required
                  background={COLOR_THEME.extras?.required_icon?.background}
                  text={COLOR_THEME.extras?.required_icon?.text}
                  border={COLOR_THEME.extras?.required_icon?.border}
                  borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
                />
                <FormBlock.Row>
                  <FormBlock.Select
                    description="都道府県"
                    value={values.shopPrefId}
                    options={shopPrefInfo}
                    onChange={ev => {
                      handleShopPref(ev.target.value as IPrefecture);
                      handleShop(null);
                    }}
                    width={isNewTheme ? "400px" : "50%"}
                    className={classNames({
                      inputCss: isNewTheme,
                    })}
                    required
                    aria-label="都道府県"
                    background={COLOR_THEME.extras?.invalid_state?.background}
                  />
                </FormBlock.Row>
                <FormBlock.Row>
                  <FormBlock.Select
                    description="店舗"
                    value={values.shopId as string}
                    options={getShopOptions(
                      contractId as number,
                      shopInfo,
                      values.shopPrefId ? switchPrefectureCode(values.shopPrefId as IPrefecture) : 0
                    )}
                    onChange={ev => handleShop(ev.target.value)}
                    width={isNewTheme ? "400px" : "50%"}
                    className={classNames({
                      inputCss: isNewTheme,
                    })}
                    required
                    aria-label="店舗"
                    background={COLOR_THEME.extras?.invalid_state?.background}
                    disabled={!values.shopPrefId || Boolean(validates.shopPrefId) || shopPrefInfo.length === 0}
                  />
                </FormBlock.Row>
              </FormBlock>
            )}

            {isReferralSource && (
              <CustomFormBlock {...formBlockProps}>
                <FormBlock.Label
                  value={referralSourceText}
                  required
                  background={COLOR_THEME.extras?.required_icon?.background}
                  text={COLOR_THEME.extras?.required_icon?.text}
                  border={COLOR_THEME.extras?.required_icon?.border}
                  borderRadius={COLOR_THEME.extras?.required_icon?.borderRadius}
                />
                <FormBlock.Row>
                  <FormBlock.Select
                    value={values.referralSourceCd}
                    options={getReferralSourceOptions(referralSource)}
                    onChange={ev => {
                      handleReferralSourceCd(ev.target.value);
                    }}
                    width={isNewTheme ? "400px" : "50%"}
                    className={classNames({
                      inputCss: isNewTheme,
                    })}
                    required
                    aria-label="どこで知ったか"
                    background={COLOR_THEME.extras?.invalid_state?.background}
                  />
                </FormBlock.Row>
              </CustomFormBlock>
            )}
          </FormContainer>
        </Article.Block>
      </Article>
    </IntersectionObserverContainer>
  );
};

export default CustomerBlock;
